/**
 * Forced / voluntary password change (api-contract.md §2.4).
 *
 * After a successful change every other session is signed out and this one carries on with the
 * fresh tokens the API returns (AUTH-04), straight to the role's home. Without them (no session to
 * replace) the user signs in again.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { changePassword } from "../../api/auth";
import { ApiError } from "../../api/errors";
import { usePasswordRules } from "../../api/usePasswordRules";
import { roleHome, useAuth } from "../../auth/AuthContext";
import { PasswordChecklist } from "../../components/ui/PasswordChecklist";
import { passesAll } from "../../lib/passwordRules";

export function ChangePassword() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passwordRules = usePasswordRules();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!passesAll(passwordRules, next)) {
      setError("Your new password doesn't meet the rules below yet.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await changePassword(current, next);
      if (user && result.access_token && result.refresh_token) {
        login({ ...user, must_change_password: false }, { access: result.access_token, refresh: result.refresh_token });
        navigate(roleHome(user.role), { replace: true });
      } else {
        // logout() routes to the role's own sign-in page.
        await logout();
      }
    } catch (err) {
      setError(
        // PASSWORD_WEAK is shown in the server's words: they state the rule it applies (AUTH-15).
        err instanceof ApiError
          ? err.code === "CURRENT_PASSWORD_INCORRECT" || err.code === "INVALID_CREDENTIALS"
            ? "Current password is incorrect."
            : err.message
          : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-dvh flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-brand">Change password</h1>
        <p className="mb-6 text-sm text-gray-500">
          You'll stay signed in here; every other device signed in to your account will be signed out.
        </p>
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Current password"
            className="w-full rounded-lg border px-3 py-2"
            required
          />
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="New password"
            className="w-full rounded-lg border px-3 py-2"
            required
          />
          <PasswordChecklist password={next} rules={passwordRules} />
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-brand py-2 text-brand-fg disabled:opacity-50"
          >
            {busy ? "Saving…" : "Change password"}
          </button>
        </form>
      </div>
    </main>
  );
}
